// v10/src/components/admin/inventory/modals/RegisterMovementModal.js
//
// Modal "Registrar movimiento" del Stock de repuestos (A8) — Tarea 2.
// POST /api/inventory-movements
//
// Formulario adaptativo según item.tracking_mode:
//   • quantity   → permite intake / dispatch / adjustment (con campos específicos)
//   • serialized → solo permite adjustment (con disclaimer)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, ArrowRightLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const formatStock = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 3, minimumFractionDigits: 0 });
};

const RegisterMovementModal = ({
  isOpen,
  item,
  aircraft = [],
  onClose,
  onSuccess,
  darkMode,
  locale = 'es-AR',
}) => {
  const { t } = useTranslation('admin');

  const isSerialized = item?.tracking_mode === 'serialized';
  const allowedTypes = isSerialized ? ['adjustment'] : ['intake', 'dispatch', 'adjustment'];

  const [movementType, setMovementType] = useState(allowedTypes[0]);
  const [direction, setDirection] = useState('add');
  const [quantity, setQuantity] = useState('');
  const [supplier, setSupplier] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [invoice, setInvoice] = useState('');
  const [aircraftId, setAircraftId] = useState('');
  const [reason, setReason] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setMovementType(allowedTypes[0]);
      setDirection('add');
      setQuantity('');
      setSupplier('');
      setUnitCost('');
      setInvoice('');
      setAircraftId('');
      setReason('');
      setError('');
    }
    // eslint-disable-next-line
  }, [isOpen, item?.id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  const validation = useMemo(() => {
    const errors = [];
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) errors.push(t('inventory_modal_movement_validation_quantity_required'));
    if (movementType === 'adjustment' && !reason.trim()) errors.push(t('inventory_modal_movement_validation_reason_required'));
    return { errors, ok: errors.length === 0 };
  }, [quantity, movementType, reason, t]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || submitting || !item?.id) return;

    const payload = {
      inventory_item_id: item.id,
      movement_type: movementType,
      quantity: Number(quantity),
    };
    if (movementType === 'intake') {
      if (supplier.trim()) payload.supplier = supplier.trim();
      if (unitCost !== '') payload.unit_cost = Number(unitCost);
      if (invoice.trim()) payload.invoice_reference = invoice.trim();
    } else if (movementType === 'dispatch') {
      if (aircraftId) payload.aircraft_id = aircraftId;
      if (reason.trim()) payload.reason = reason.trim();
    } else {
      payload.direction = direction;
      payload.reason = reason.trim();
    }

    setSubmitting(true);
    setError('');
    try {
      await inventoryService.createMovement(payload);
      if (!isMounted.current) return;
      onSuccess?.();
      onClose?.();
    } catch (err) {
      if (!isMounted.current) return;
      const msg = err?.response?.data?.message || t('inventory_modal_network_error');
      setError(msg);
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  if (!isOpen || !item) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };

  const stockUnitLabel = item.stock_unit
    ? t(`inventory_units_${item.stock_unit}`, { defaultValue: item.stock_unit })
    : '';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={submitting ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#0891b2,#0e7490)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(8,145,178,0.3)',
            }}>
              <ArrowRightLeft style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('inventory_modal_movement_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('inventory_modal_movement_subtitle', { name: item.name })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={submitting}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: submitting ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{
          padding: '10px 14px', borderRadius: 12, marginBottom: 14,
          background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
        }}>
          <p style={{ margin: 0, fontSize: 12, color: fgMuted }}>
            {t('inventory_modal_movement_current_stock', {
              stock: formatStock(item.current_stock_quantity, locale),
              unit: stockUnitLabel,
            })}
          </p>
        </div>

        {isSerialized && (
          <div style={{
            padding: '10px 14px', borderRadius: 12, marginBottom: 14,
            background: 'rgba(13,148,136,0.10)', border: '1px solid rgba(13,148,136,0.25)',
            color: '#0d9488',
          }}>
            <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>
              {t('inventory_modal_movement_serialized_disclaimer')}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Tipo de movimiento */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_movement_type_label')}</p>
            <div style={{
              display: 'inline-flex', padding: 4, borderRadius: 12, gap: 4, flexWrap: 'wrap',
              background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
              border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
            }}>
              {allowedTypes.map((tp) => {
                const isSel = tp === movementType;
                return (
                  <button
                    key={tp} type="button"
                    onClick={() => setMovementType(tp)}
                    style={{
                      padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
                      background: isSel ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white') : 'transparent',
                      color: isSel ? (darkMode ? 'white' : '#0f172a') : fgMuted,
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                      boxShadow: isSel ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                    }}
                  >
                    {t(`inventory_modal_movement_type_${tp}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Adjustment direction */}
          {movementType === 'adjustment' && (
            <div style={{ marginBottom: 14 }}>
              <p style={labelStyle}>{t('inventory_modal_movement_adjustment_direction_label')}</p>
              <div style={{
                display: 'inline-flex', padding: 4, borderRadius: 12, gap: 4,
                background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
              }}>
                {['add', 'subtract'].map((d) => {
                  const isSel = d === direction;
                  return (
                    <button
                      key={d} type="button"
                      onClick={() => setDirection(d)}
                      style={{
                        padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: isSel ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white') : 'transparent',
                        color: isSel ? (darkMode ? 'white' : '#0f172a') : fgMuted,
                        fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                      }}
                    >
                      {t(`inventory_modal_movement_adjustment_direction_${d}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cantidad */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>
              {t('inventory_modal_movement_quantity_label')}
              {stockUnitLabel ? ` (${stockUnitLabel})` : ''}
            </p>
            <input
              type="number" min="0" step="0.01" style={inputStyle}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={t('inventory_modal_movement_quantity_placeholder')}
            />
          </div>

          {/* Intake fields */}
          {movementType === 'intake' && (
            <>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_movement_intake_supplier_label')}</p>
                <input
                  type="text" maxLength={150} style={inputStyle}
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  placeholder={t('inventory_modal_movement_intake_supplier_placeholder')}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_movement_intake_unit_cost_label')}</p>
                  <input
                    type="number" min="0" step="0.01" style={inputStyle}
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    placeholder={t('inventory_modal_movement_intake_unit_cost_placeholder')}
                  />
                </div>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_movement_intake_invoice_label')}</p>
                  <input
                    type="text" maxLength={100} style={inputStyle}
                    value={invoice}
                    onChange={(e) => setInvoice(e.target.value)}
                    placeholder={t('inventory_modal_movement_intake_invoice_placeholder')}
                  />
                </div>
              </div>
            </>
          )}

          {/* Dispatch fields */}
          {movementType === 'dispatch' && (
            <>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_movement_dispatch_aircraft_label')}</p>
                <select
                  value={aircraftId}
                  onChange={(e) => setAircraftId(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('inventory_modal_movement_dispatch_aircraft_none')}</option>
                  {aircraft.map((ac) => (
                    <option key={ac.id} value={ac.id}>
                      {ac.registration} — {ac.manufacturer ? `${ac.manufacturer} ` : ''}{ac.model_name || ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_movement_reason_label')}</p>
                <input
                  type="text" maxLength={200} style={inputStyle}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('inventory_modal_movement_reason_placeholder_dispatch')}
                />
              </div>
            </>
          )}

          {/* Adjustment reason */}
          {movementType === 'adjustment' && (
            <div style={{ marginBottom: 14 }}>
              <p style={labelStyle}>{t('inventory_modal_movement_reason_label')}</p>
              <textarea
                rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('inventory_modal_movement_reason_placeholder_adjustment')}
              />
            </div>
          )}

          {/* Validation errors */}
          {validation.errors.length > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              {validation.errors.map((err, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {err}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={submitting || !validation.ok}
              whileHover={!submitting && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!submitting && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: submitting || !validation.ok ? 'not-allowed' : 'pointer',
                background: submitting || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#0891b2,#0e7490)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {submitting
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <ArrowRightLeft style={{ width: 16, height: 16 }} />
              }
              {submitting
                ? t('inventory_modal_movement_action_loading')
                : t('inventory_modal_movement_action')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={submitting}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
              }}
            >
              {t('inventory_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default RegisterMovementModal;
